import type { TenantTheme } from '../types/contracts';

export interface ResolvedTheme {
  primary: string;
  onPrimary: string;
  /** Primary color adjusted so that, used as text on white, it reaches 4.5:1. */
  primaryText: string;
  position: 'bottom-right' | 'bottom-left';
  radius: string;
}

export const DEFAULT_PRIMARY = '#1f4fd1';

function parseHex(color: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return null;
  let hex = m[1]!;
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  const n = parseInt(hex, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
}

export function relativeLuminance(color: string): number {
  const rgb = parseHex(color);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Picks white or near-black text, whichever has the higher contrast on `bg`. */
export function readableOn(bg: string): string {
  return contrastRatio(bg, '#ffffff') >= contrastRatio(bg, '#111111') ? '#ffffff' : '#111111';
}

/** Darkens a color until it reaches `min` contrast against `against` (default white). */
export function ensureContrast(color: string, against = '#ffffff', min = 4.5): string {
  let rgb = parseHex(color);
  if (!rgb) return DEFAULT_PRIMARY;
  for (let i = 0; i < 20 && contrastRatio(toHex(rgb), against) < min; i++) {
    rgb = rgb.map((v) => v * 0.85) as [number, number, number];
  }
  return toHex(rgb);
}

function normalizeRadius(radius: TenantTheme['radius']): string {
  if (typeof radius === 'number' && Number.isFinite(radius)) return `${Math.max(0, Math.min(32, radius))}px`;
  if (typeof radius === 'string' && /^\d+(\.\d+)?(px|rem|em)$/.test(radius.trim())) return radius.trim();
  return '16px';
}

/** Merge order: defaults < tenant theme (session) < integrator overrides (init config). */
export function resolveTheme(...themes: (TenantTheme | undefined | null)[]): ResolvedTheme {
  const merged: TenantTheme = Object.assign({}, ...themes.filter(Boolean));
  const primaryRaw = typeof merged.primary_color === 'string' && parseHex(merged.primary_color) ? merged.primary_color : DEFAULT_PRIMARY;
  const primary = toHex(parseHex(primaryRaw)!);
  // The launcher/header background must allow a 4.5:1 foreground: if neither white nor black text
  // reaches it (mid-tones), darken the primary until white text does.
  const bg = contrastRatio(primary, readableOn(primary)) >= 4.5 ? primary : ensureContrast(primary, '#ffffff');
  return {
    primary: bg,
    onPrimary: readableOn(bg),
    primaryText: ensureContrast(primary, '#ffffff'),
    position: merged.position === 'bottom-left' ? 'bottom-left' : 'bottom-right',
    radius: normalizeRadius(merged.radius),
  };
}

export function themeToCssVars(t: ResolvedTheme): string {
  return `--aca-primary:${t.primary};--aca-on-primary:${t.onPrimary};--aca-primary-text:${t.primaryText};--aca-radius:${t.radius};`;
}
